import { Button, Checkbox } from "antd";
import to from "await-to-js";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { RangePicker } from "../../../components/DatePicker/DatePicker";
import { Input } from "../../../components/Input/Input";
import { PopconfirmButton } from "../../../components/PopconfirmButton/PopconfirmButton";
import { Table } from "../../../components/Table/Table";
import { ModalsContext } from "../../../context/modals.context";
import { DATE_FORMAT } from "../../../core/constants";
import { MinistryContext } from "../../../modules/ministry/ministry.context";
import { ministryService } from "../../../modules/ministry/ministry.service";
import {
  IMfSveMarkiceVracenihPayload,
  IMfSveMarkiceVracenihResponse,
} from "../../../modules/ministry/ministry.types";
import { formatDateDDMMYY } from "../../../utils/dateFormatter";
import { AddEditRecordModal } from "./components/AddEditRecordModal/AddEditRecordModal";
import { DestroyRecordsModal } from "./components/DestroyRecordsModal/DestroyRecordsModal";
import style from "./ReturnedStampsRecords.module.scss";

const INITIAL_PARAMS = (): IMfSveMarkiceVracenihPayload => ({
  brojZahteva: null,
  datumVracanja: null,
  pib: null,
  pageNumber: 1,
  pageSize: 20,
});

export const ReturnedStampsRecords = () => {
  const { ministry, setMinistry } = useContext(MinistryContext);
  const { handleUpdateModals, setError } = useContext(ModalsContext);
  const [params, setParams] = useState(INITIAL_PARAMS());
  const [checkedBoxes, setCheckedBoxes] = useState<any>([]);

  const postMfSveMarkiceVracenih = useDebouncedCallback(async () => {
    const [err, res] = await to(
      ministryService.postMfSveMarkiceVracenih(params)
    );

    if (err || !res.success) return setError(res?.errorMessage);

    setMinistry({
      markiceVracenih: res.data,
    });

    setCheckedBoxes([]);
  }, 400);

  const postMfMarkiceVraceneDelete = async (id: number) => {
    const [err, res] = await to(
      ministryService.postMfMarkiceVraceneDelete({ id })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    postMfSveMarkiceVracenih();
  };

  const handleCheckboxChange = (event: any, id: number | null) => {
    if (event.target.checked) {
      setCheckedBoxes([...checkedBoxes, id]);
    } else {
      setCheckedBoxes(checkedBoxes.filter((item: any) => item !== id));
    }
  };

  const updateParams = (
    accessor: keyof any,
    value: string | null | number | (string | undefined)[]
  ) => {
    setParams({ ...params, [accessor]: value || null });
  };

  const colDef = [
    {
      width: 35,
      dataIndex: "checkbox",
      render: (_text: any, record: any) => (
        <Checkbox
          onClick={(e) => {
            e.stopPropagation();
            handleCheckboxChange(e, record.id);
          }}
        />
      ),
    },
    { title: t("AKCIZNE_MARKICE.NAZIV"), dataIndex: "naziv" },
    { title: t("AKCIZNE_MARKICE.PIB"), dataIndex: "pib" },
    {
      title: t("EVIDENCIJA_MARKICA.KOLICINA"),
      dataIndex: "kolicina",
    },
    {
      title: t("TABLES.ZAM_ID_ZAHTEVA"),
      dataIndex: "brojZahteva",
    },
    {
      title: t("TABLES.VRSTA_KATEGORIJE"),
      dataIndex: "vrstaKategorije",
    },
    {
      title: t("TABLES.DATUM_VRACANJA"),
      dataIndex: "datumVracanja",
      render: (datum: string) => formatDateDDMMYY(datum),
    },
    {
      title: t("TABLES.DATUM_UNISTAVANJA"),
      dataIndex: "datumUnistavanja",
      render: (datum: string) => formatDateDDMMYY(datum),
    },
    {
      title: t("COMMON.IZBRISI"),
      width: 150,
      render: (_text: any, record: IMfSveMarkiceVracenihResponse) => (
        <PopconfirmButton
          title={t("POPCONFIRM.IZBRISI_VRACENE")}
          onConfirm={(e) => {
            e.stopPropagation();
            postMfMarkiceVraceneDelete(record.id);
          }}
          children={
            <Button
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
              }}
              danger
            >
              {t("COMMON.IZBRISI")}
            </Button>
          }
        />
      ),
    },
  ];

  const onRowHandler = (record: IMfSveMarkiceVracenihResponse) => ({
    onClick: () => {
      handleUpdateModals({
        modalVisible: "dodajIzmeniEvidencijuModal",
        id: record.id,
        record: record,
      });
    },
  });

  const onPageChangeHandler = (pageNumber: number, pageSize: number) => {
    if (pageNumber !== params?.pageNumber && Boolean(params.pageNumber)) {
      updateParams("pageNumber", pageNumber);
    }

    if (pageSize !== params?.pageSize && Boolean(params.pageNumber)) {
      updateParams("pageSize", pageSize);
    }
  };

  useEffect(() => {
    postMfSveMarkiceVracenih();
  }, [params]);

  return (
    <div className={style.mainContainer}>
      <DestroyRecordsModal refetch={postMfSveMarkiceVracenih} />
      <AddEditRecordModal refetch={postMfSveMarkiceVracenih} />
      <div id="ministry_returned_stamps_records">
        <h2>{t("NAVIGATION.EVIDENCIJA_VRACENIH_MARKICA")}</h2>
        <div className={style.buttonSubcontainer}>
          <RangePicker
            allowClear
            className="w-35"
            format={DATE_FORMAT}
            onChange={(value) => {
              const updatedValue = [
                value?.[0]?.format("YYYY-MM-DDT00:00:00[Z]"),
                value?.[1]?.format("YYYY-MM-DDT23:59:59[Z]"),
              ];
              updateParams("datumVracanja", updatedValue);
            }}
            placeholder={[
              t("PLACEHOLDERS.DATUM_OD"),
              t("PLACEHOLDERS.DATUM_DO"),
            ]}
            name="evidencija-vracenih-datum"
          />
          <Input
            onChange={({ target }) => updateParams("brojZahteva", target.value)}
            allowClear
            className="w-16"
            placeholder={t("TABLES.BROJ_ZAHTEVA")}
            name="broj-zahteva"
          />
          <Input
            onChange={({ target }) => updateParams("pib", target.value)}
            allowClear
            className="w-16"
            placeholder={t("PLACEHOLDERS.PIB")}
            name="pib"
          />
          <Button
            className="w-18"
            type="primary"
            onClick={() =>
              handleUpdateModals({
                modalVisible: "dodajIzmeniEvidencijuModal",
              })
            }
          >
            {t("AKCIZNE_MARKICE.KREIRAJ_NOVI_ZAHTEV")}
          </Button>
          <Button
            className="w-12"
            type="primary"
            disabled={!checkedBoxes.length}
            onClick={() =>
              handleUpdateModals({
                modalVisible: "unistiEvidencijuModal",
                data: checkedBoxes,
              })
            }
            danger
          >
            {t("AKCIZNE_MARKICE.UNISTI")}
          </Button>
        </div>
      </div>
      <Table
        name="mfin-evidencija-vracenih-markica-tabela"
        columns={colDef}
        scrollConfig={["#ministry_returned_stamps_records"]}
        dataSource={ministry.markiceVracenih}
        onRow={onRowHandler}
        rowClassName="pointer"
        pagination={{
          onChange: onPageChangeHandler,
        }}
      />
    </div>
  );
};
